import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FinancingBanner from '../../../components/feature/FinancingBanner';

export default function DeerDeterrentPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-red-100 text-red-800 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                Protect What You've Grown
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Residential & Specialty Crop Deer Deterrent
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Deer don't just nibble—they devastate. From backyard gardens to commercial Christmas tree farms, unchecked deer pressure destroys years of investment in a single season.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our aerial deterrent application creates an invisible barrier that keeps deer away from your most valuable plantings—without fencing, without chemicals that harm your plants, and without constant reapplication.
              </p>
              <a
                href="/#contact"
                className="inline-block bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Get a Free Assessment
              </a>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/ba53e88899bdf94fba8fc8178de9a73d.jpg"
                alt="Deer actively damaging residential landscaping"
                className="w-full h-[500px] object-cover object-top rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-4 rounded-lg shadow-lg">
                <div className="text-3xl font-bold">$500+</div>
                <div className="text-sm">Average damage per yard annually</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">The Deer Damage Problem Is Real</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Every year, deer cause billions of dollars in damage to residential landscapes, nurseries, and specialty crops across the Mid-Atlantic. Here's what unchecked deer pressure looks like.
            </p>
          </div>

          {/* Residential Damage Gallery */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold mb-8 flex items-center">
              <i className="ri-home-4-line text-3xl text-red-600 mr-3"></i>
              Residential Landscape Damage
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="relative group">
                <img
                  src="https://readdy.ai/api/search-image?query=Close-up%20of%20severely%20damaged%20hosta%20plants%20in%20residential%20garden%20bed%20with%20deer%20bite%20marks%20clearly%20visible%20on%20leaves%2C%20torn%20and%20shredded%20foliage%2C%20some%20stems%20completely%20stripped%20bare%2C%20mulched%20garden%20bed%20setting%2C%20natural%20daylight%20showing%20clear%20evidence%20of%20deer%20browsing%20damage&width=500&height=400&seq=hosta-damage-001&orientation=landscape"
                  alt="Deer-damaged hostas showing bite marks and stripped leaves"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Hostas stripped overnight—a common sight in deer-heavy areas</p>
                </div>
              </div>
              
              <div className="relative group">
                <img
                  src="https://readdy.ai/api/search-image?query=Deer%20actively%20browsing%20on%20residential%20azalea%20bushes%20and%20rhododendrons%20near%20house%20foundation%2C%20deer%20head%20down%20eating%20flowering%20shrubs%2C%20visible%20damage%20to%20ornamental%20plants%2C%20suburban%20backyard%20setting%20with%20fence%20in%20background%2C%20natural%20daylight%20realistic%20wildlife%20photography&width=500&height=400&seq=azalea-deer-001&orientation=landscape"
                  alt="Deer feeding on azaleas near home foundation"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Azaleas and rhododendrons—deer favorites that rarely recover</p>
                </div>
              </div>
              
              <div className="relative group">
                <img
                  src="https://readdy.ai/api/search-image?query=Damaged%20residential%20rose%20garden%20with%20deer%20browsing%20damage%2C%20rose%20bushes%20with%20stripped%20leaves%20and%20eaten%20flower%20buds%2C%20thorny%20stems%20remaining%20but%20foliage%20gone%2C%20garden%20bed%20with%20mulch%20showing%20deer%20hoof%20prints%2C%20natural%20daylight%20showing%20clear%20plant%20damage&width=500&height=400&seq=rose-damage-001&orientation=landscape"
                  alt="Rose garden devastated by deer browsing"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Even thorny roses can't escape persistent deer pressure</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Wide%20shot%20of%20suburban%20front%20yard%20showing%20extensive%20deer%20damage%20to%20landscaping%2C%20multiple%20ornamental%20shrubs%20and%20perennials%20eaten%20down%20to%20stems%2C%20patchy%20lawn%20with%20deer%20trails%20visible%2C%20nice%20house%20in%20background%20contrasting%20with%20damaged%20yard%2C%20natural%20daylight%20realistic%20scene&width=700&height=400&seq=yard-damage-001&orientation=landscape"
                  alt="Wide view of deer-damaged residential landscaping"
                  className="w-full h-[350px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white font-medium">Thousands of dollars in landscaping—destroyed in weeks</p>
                </div>
              </div>
              
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20deer%20standing%20in%20residential%20vegetable%20garden%20eating%20tomato%20plants%20and%20bean%20vines%2C%20clear%20damage%20to%20garden%20vegetables%2C%20raised%20bed%20garden%20setting%20in%20suburban%20backyard%2C%20deer%20actively%20feeding%20on%20crops%2C%20natural%20morning%20light%20realistic%20wildlife%20photography&width=700&height=400&seq=veggie-deer-001&orientation=landscape"
                  alt="Deer raiding a residential vegetable garden"
                  className="w-full h-[350px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white font-medium">Vegetable gardens are easy targets—deer return nightly</p>
                </div>
              </div>
            </div>
          </div>

          {/* Christmas Tree Farm Damage */}
          <div>
            <h3 className="text-2xl font-bold mb-8 flex items-center">
              <i className="ri-tree-line text-3xl text-red-600 mr-3"></i>
              Christmas Tree & Specialty Crop Damage
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Deer%20browsing%20on%20young%20Christmas%20trees%20at%20tree%20farm%2C%20deer%20actively%20eating%20the%20tender%20tips%20and%20branches%20of%20Fraser%20fir%20or%20Douglas%20fir%20saplings%2C%20rows%20of%20Christmas%20trees%20in%20background%20with%20visible%20browse%20damage%2C%20rural%20farm%20setting%20natural%20daylight&width=500&height=400&seq=xmas-deer-001&orientation=landscape"
                  alt="Deer feeding on Christmas tree farm saplings"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Young trees are most vulnerable—one browse can ruin years of growth</p>
                </div>
              </div>
              
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Close-up%20of%20Christmas%20tree%20with%20severe%20deer%20browse%20damage%20showing%20stripped%20branches%20and%20missing%20terminal%20leader%2C%20damaged%20Fraser%20fir%20or%20pine%20tree%20with%20bare%20spots%20and%20uneven%20growth%20from%20deer%20feeding%2C%20tree%20farm%20setting%20natural%20daylight&width=500&height=400&seq=xmas-damage-001&orientation=landscape"
                  alt="Christmas tree with browse damage and missing leader"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Browse damage creates unsellable trees—lost revenue for years</p>
                </div>
              </div>
              
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Row%20of%20Christmas%20trees%20at%20farm%20showing%20uneven%20deer%20damage%2C%20some%20trees%20with%20browse%20lines%20and%20stripped%20lower%20branches%2C%20comparison%20of%20damaged%20and%20undamaged%20trees%20side%20by%20side%2C%20commercial%20tree%20farm%20setting%20with%20natural%20daylight&width=500&height=400&seq=xmas-row-001&orientation=landscape"
                  alt="Row of Christmas trees showing varying deer damage"
                  className="w-full h-[300px] object-cover object-top rounded-lg shadow-md"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                  <p className="text-white text-sm font-medium">Inconsistent damage makes entire blocks unmarketable</p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
              <div className="flex items-start">
                <i className="ri-error-warning-line text-3xl text-red-600 mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-lg mb-2">The Real Cost of Deer Damage</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Christmas tree growers in Pennsylvania and Maryland report <strong>15-30% crop loss</strong> annually from deer browse. A single deer can damage 20+ trees per night during peak feeding. For a 10-acre operation, that's <strong>$10,000–$25,000 in lost revenue</strong> every year.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Deterrent Works Section */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Aerial Deterrent Application Works</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Traditional deer control methods—fencing, sprays, scare devices—are expensive, labor-intensive, and often ineffective. Our aerial application delivers professional-grade deterrent across your entire property in a single visit.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-800 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-drone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Complete Coverage</h4>
                    <p className="text-gray-600">Aerial application reaches every plant, every row, every corner—no gaps for deer to exploit.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-800 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Fast Application</h4>
                    <p className="text-gray-600">Treat acres in hours, not days. No trampling your plants or compacting your soil.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-800 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-leaf-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Plant-Safe Formula</h4>
                    <p className="text-gray-600">Our deterrent is safe for ornamentals, edibles, and Christmas trees. No residue, no harm.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-emerald-800 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-calendar-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Seasonal Programs</h4>
                    <p className="text-gray-600">Scheduled applications during peak pressure periods keep protection consistent all season.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20agricultural%20drone%20flying%20low%20over%20ornamental%20nursery%20applying%20spray%20treatment%2C%20visible%20mist%20from%20drone%20sprayer%20over%20rows%20of%20shrubs%20and%20young%20trees%2C%20operator%20in%20safety%20vest%20monitoring%20from%20ground%2C%20commercial%20nursery%20setting%20natural%20daylight%20professional%20photography&width=700&height=400&seq=drone-nursery-001&orientation=landscape"
                alt="Drone applying deterrent over nursery"
                className="w-full h-[350px] object-cover object-top rounded-xl shadow-lg"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Healthy%20protected%20Christmas%20tree%20farm%20with%20lush%20green%20Fraser%20fir%20trees%20in%20neat%20rows%2C%20no%20deer%20damage%20visible%2C%20thriving%20commercial%20tree%20operation%20in%20Mid-Atlantic%20region%2C%20rolling%20hills%20in%20background%2C%20natural%20daylight%20showing%20healthy%20crop&width=700&height=300&seq=healthy-xmas-001&orientation=landscape"
                alt="Protected Christmas tree farm with healthy trees"
                className="w-full h-[280px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Comparison Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-800 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              See the Difference
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Protected vs. Unprotected</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              The results speak for themselves. Compare properties with and without our aerial deterrent program.
            </p>
          </div>

          {/* Comparison 1: Residential Landscaping */}
          <div className="mb-16">
            <h3 className="text-xl font-bold mb-6 text-center text-gray-800">Residential Landscaping</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  UNPROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Severely%20damaged%20residential%20front%20yard%20landscaping%20with%20deer%20browse%20damage%2C%20hostas%20eaten%20to%20ground%20level%2C%20azaleas%20stripped%20of%20leaves%2C%20ornamental%20shrubs%20with%20bare%20branches%2C%20patchy%20destroyed%20garden%20beds%20around%20nice%20suburban%20home%2C%20evidence%20of%20repeated%20deer%20feeding%2C%20natural%20daylight%20showing%20devastation&width=600&height=450&seq=before-residential-001&orientation=landscape"
                  alt="Unprotected residential landscaping with severe deer damage"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-red-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-close-circle-fill text-red-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">Without Protection</span>
                  </div>
                  <p className="text-red-100 text-sm">Hostas eaten to the ground, shrubs stripped bare, years of landscaping destroyed in weeks</p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  PROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20thriving%20residential%20front%20yard%20landscaping%20with%20lush%20healthy%20hostas%2C%20full%20blooming%20azaleas%20and%20rhododendrons%2C%20vibrant%20ornamental%20shrubs%20and%20perennials%2C%20well-maintained%20garden%20beds%20around%20suburban%20home%2C%20no%20deer%20damage%20visible%2C%20natural%20daylight%20showing%20healthy%20flourishing%20plants&width=600&height=450&seq=after-residential-001&orientation=landscape"
                  alt="Protected residential landscaping thriving without deer damage"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-checkbox-circle-fill text-emerald-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">With Deterrent Program</span>
                  </div>
                  <p className="text-emerald-100 text-sm">Full, healthy plants all season long—deer stay away, your investment stays intact</p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison 2: Christmas Tree Farm */}
          <div className="mb-16">
            <h3 className="text-xl font-bold mb-6 text-center text-gray-800">Christmas Tree Farm</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  UNPROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Christmas%20tree%20farm%20with%20severe%20deer%20browse%20damage%2C%20young%20Fraser%20fir%20trees%20with%20stripped%20branches%20and%20missing%20terminal%20leaders%2C%20uneven%20scraggly%20growth%20from%20repeated%20deer%20feeding%2C%20some%20trees%20completely%20ruined%20and%20unsellable%2C%20commercial%20tree%20farm%20showing%20crop%20loss%2C%20natural%20daylight&width=600&height=450&seq=before-xmas-farm-001&orientation=landscape"
                  alt="Unprotected Christmas tree farm with deer damage"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-red-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-close-circle-fill text-red-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">Without Protection</span>
                  </div>
                  <p className="text-red-100 text-sm">20-30% of trees unsellable due to browse damage, missing leaders, and uneven growth</p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  PROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20healthy%20Christmas%20tree%20farm%20with%20perfect%20Fraser%20fir%20trees%20in%20neat%20rows%2C%20full%20dense%20foliage%20with%20no%20deer%20damage%2C%20uniform%20growth%20with%20intact%20terminal%20leaders%2C%20premium%20quality%20sellable%20trees%2C%20thriving%20commercial%20tree%20operation%2C%20natural%20daylight%20showing%20healthy%20crop&width=600&height=450&seq=after-xmas-farm-001&orientation=landscape"
                  alt="Protected Christmas tree farm with healthy trees"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-checkbox-circle-fill text-emerald-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">With Deterrent Program</span>
                  </div>
                  <p className="text-emerald-100 text-sm">Full, symmetrical trees with intact leaders—premium quality, maximum sellable inventory</p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison 3: Vegetable Garden */}
          <div className="mb-12">
            <h3 className="text-xl font-bold mb-6 text-center text-gray-800">Home Vegetable Garden</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  UNPROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Destroyed%20home%20vegetable%20garden%20with%20severe%20deer%20damage%2C%20tomato%20plants%20eaten%20and%20broken%2C%20bean%20vines%20stripped%20bare%2C%20lettuce%20and%20greens%20completely%20consumed%2C%20raised%20bed%20garden%20showing%20devastation%20from%20deer%20feeding%2C%20suburban%20backyard%20setting%2C%20natural%20daylight&width=600&height=450&seq=before-veggie-001&orientation=landscape"
                  alt="Unprotected vegetable garden destroyed by deer"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-red-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-close-circle-fill text-red-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">Without Protection</span>
                  </div>
                  <p className="text-red-100 text-sm">Entire harvest lost overnight—deer return every night until nothing remains</p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute top-4 left-4 z-10 bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  PROTECTED
                </div>
                <img
                  src="https://readdy.ai/api/search-image?query=Thriving%20abundant%20home%20vegetable%20garden%20with%20healthy%20tomato%20plants%20full%20of%20ripe%20tomatoes%2C%20lush%20bean%20vines%2C%20vibrant%20lettuce%20and%20greens%2C%20productive%20raised%20bed%20garden%20in%20suburban%20backyard%2C%20no%20deer%20damage%2C%20bountiful%20harvest%20ready%2C%20natural%20daylight%20showing%20healthy%20vegetables&width=600&height=450&seq=after-veggie-001&orientation=landscape"
                  alt="Protected vegetable garden thriving with full harvest"
                  className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-900/90 to-transparent p-6 rounded-b-xl">
                  <div className="flex items-center mb-2">
                    <i className="ri-checkbox-circle-fill text-emerald-400 text-xl mr-2"></i>
                    <span className="text-white font-semibold">With Deterrent Program</span>
                  </div>
                  <p className="text-emerald-100 text-sm">Enjoy the full harvest you worked for—fresh vegetables all season long</p>
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-2xl p-8 md:p-10 text-white">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-emerald-300"></i>
                </div>
                <div className="text-3xl font-bold mb-1">85%</div>
                <div className="text-emerald-200 text-sm">Less Browse Damage</div>
              </div>
              <div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-emerald-300"></i>
                </div>
                <div className="text-3xl font-bold mb-1">10x</div>
                <div className="text-emerald-200 text-sm">ROI on Protection</div>
              </div>
              <div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-plant-line text-2xl text-emerald-300"></i>
                </div>
                <div className="text-3xl font-bold mb-1">95%</div>
                <div className="text-emerald-200 text-sm">Plant Survival Rate</div>
              </div>
              <div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-emotion-happy-line text-2xl text-emerald-300"></i>
                </div>
                <div className="text-3xl font-bold mb-1">100%</div>
                <div className="text-emerald-200 text-sm">Customer Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-emerald-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">85%</div>
              <div className="text-emerald-200">Reduction in browse damage</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">4-6</div>
              <div className="text-emerald-200">Weeks of protection per application</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">$15</div>
              <div className="text-emerald-200">Average cost per acre</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">24hr</div>
              <div className="text-emerald-200">Typical response time</div>
            </div>
          </div>
          <p className="text-center text-emerald-200 mt-8 text-sm">
            Want to calculate your potential savings? <a href="/roi-calculator" className="text-white hover:text-emerald-100 font-semibold underline">Use our ROI calculator</a> to see how much you could recover per acre.
          </p>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">Who We Serve</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-stone-50 p-8 rounded-xl">
              <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Homeowners</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Protect your landscaping investment. From foundation plantings to vegetable gardens, we keep deer away from what you've worked hard to grow.
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Ornamental gardens</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Vegetable plots</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Foundation shrubs</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Fruit trees</li>
              </ul>
            </div>
            
            <div className="bg-stone-50 p-8 rounded-xl">
              <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-tree-line text-3xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Christmas Tree Farms</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Protect your crop from browse damage that creates unsellable trees. Seasonal programs timed to peak deer pressure periods.
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Fraser fir</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Douglas fir</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Blue spruce</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>White pine</li>
              </ul>
            </div>
            
            <div className="bg-stone-50 p-8 rounded-xl">
              <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-plant-line text-3xl text-emerald-800"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Nurseries & Orchards</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Commercial operations need commercial-grade protection. We cover large acreage quickly and efficiently.
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Ornamental nurseries</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Apple orchards</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Vineyards</li>
                <li className="flex items-center"><i className="ri-check-line text-emerald-600 mr-2"></i>Berry farms</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-10 rounded-2xl shadow-lg">
            <div className="flex items-start mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Portrait%20of%20middle-aged%20man%20in%20casual%20outdoor%20clothing%20standing%20in%20Christmas%20tree%20farm%2C%20friendly%20expression%2C%20natural%20daylight%2C%20professional%20headshot%20style%20with%20trees%20in%20soft%20focus%20background&width=120&height=120&seq=farmer-portrait-001&orientation=squarish"
                alt="Tom Brennan"
                className="w-20 h-20 rounded-full object-cover object-top mr-6"
              />
              <div>
                <div className="text-xl font-bold">Tom Brennan</div>
                <div className="text-gray-600">Brennan's Christmas Tree Farm, Frederick County, MD</div>
                <div className="flex items-center mt-2">
                  <i className="ri-star-fill text-amber-400"></i>
                  <i className="ri-star-fill text-amber-400"></i>
                  <i className="ri-star-fill text-amber-400"></i>
                  <i className="ri-star-fill text-amber-400"></i>
                  <i className="ri-star-fill text-amber-400"></i>
                </div>
              </div>
            </div>
            <blockquote className="text-xl text-gray-700 leading-relaxed italic">
              "We were losing 20% of our young trees to deer every year. After two seasons with Greenfield's deterrent program, browse damage dropped to almost nothing. The drone application is fast, thorough, and the trees look better than they have in years. Best investment we've made."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <FinancingBanner context="Deer damage costs more every day you wait. Our financing options let you protect your landscaping, trees, and crops now — and pay over time with flexible terms." />

      {/* CTA Section */}
      <section className="py-20 px-6 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Stop Deer Damage Before It Starts</h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Don't wait until your landscaping is destroyed or your crop is ruined. Get a free assessment and see how aerial deterrent application can protect your investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              className="inline-block bg-white text-emerald-900 font-bold px-8 py-4 rounded-md hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get a Free Assessment
            </a>
            <a
              href="tel:+17179131780"
              className="inline-flex items-center justify-center bg-emerald-800 text-white font-bold px-8 py-4 rounded-md hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 717-913-1780
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
